<template>
  <div :class="[{ '--vertical': vertical }, 'v-tabbar__content']">
    <div
      v-if="toolbar.logo"
      class="v-tabbar__logo"
      :style="{ backgroundColor: toolbar.background_color }"
    >
      <v-link to="/">
        <toolbar-logo :logo="toolbar.logo" :styles="styles" no-height />
      </v-link>
    </div>

    <div class="v-tabbar__navigation-wrapper">
      <div class="container d-flex w-100">
        <template v-if="isRootPresent && vertical">
          <div
            class="v-tabbar__item"
            :data-mvt="MVT_SELECTORS.TABBAR_HOME"
            @click="() => handleClick(homeLink)"
          >
            <v-link to="/" :page-id="homeLink.page_id">
              <v-icon icon="home" />
              <span class="tabbar__text">
                {{ $t("components.tabbarContent.home") }}
              </span>
            </v-link>
          </div>
        </template>
        <template v-else>
          <div
            v-for="link in tabbarLinks"
            :key="link.path"
            class="v-tabbar__item"
            :data-mvt="linkDataSelector(link)"
            @click="() => handleClick(link)"
            data-role="tabbar-item"
            :data-page-id="link.page_id"
            :data-page-path="link.path"
          >
            <v-link
              :class="[{ '--active': isActive(link) }]"
              :to="link.path"
              :page-id="link.page_id"
            >
              <v-icon :icon="link.icon" />
              <span class="tabbar__text">{{ link.text }}</span>
            </v-link>
            <unread-marker-dot v-if="isMessageTabbar(link)" />
          </div>
        </template>
      </div>
    </div>

    <navigation-page-container v-if="showPages" class="px-1 pb-4 pt-1" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex"

import { MVT_SELECTORS } from "@/lib/globals"
import ToolbarLogo, { DEFAULT_STYLES } from "@/components/toolbar/Logo"
import { isPresent } from "@/lib/utils"
import { trackGTMEvent, GTM_EVENTS } from "@/lib/gtm-utils"
import { withRatio } from "@shared/block-ratio"
import { standardizeUrl } from "@/lib/url-helpers"

const MESSAGES_PATH = "/me/messages"

export default {
  components: {
    ToolbarLogo,
    NavigationPageContainer: () =>
      import("@/components/NavigationPageContainer"),
    UnreadMarkerDot: () => import("@/components/message-thread/UnreadMarkerDot")
  },

  props: {
    vertical: {
      type: Boolean,
      default: true
    },
    showPages: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      MVT_SELECTORS
    }
  },

  computed: {
    ...mapState("tabbar", ["showSidebar"]),
    ...mapGetters("tabbar", ["tabbarLinks"]),
    ...mapState(["global"]),
    ...mapState("pages", ["currentPage"]),

    toolbar() {
      return this.global?.toolbar || {}
    },

    styles() {
      return {
        ...DEFAULT_STYLES,
        height: withRatio(50, this.toolbar.logo_height_ratio, { unit: "%" })
      }
    },

    isRootPresent() {
      return !!this.currentPage?.contentRootId
    },

    homeLink() {
      return this.tabbarLinks.find(link => link.path === "/") || {}
    }
  },

  methods: {
    isPresent,

    ...mapMutations("tabbar", ["toggleSidebar", "setShowSidebar"]),

    isActive(link) {
      return link.path === "/"
        ? this.$route.path === "/"
        : this.$route.path.startsWith(link.path)
    },

    isMessageTabbar(link) {
      return link.path === MESSAGES_PATH
    },

    handleClick(link) {
      trackGTMEvent(GTM_EVENTS.NAVIGATION_ITEM_CLICK, {
        data: {
          text: link.text,
          href: standardizeUrl(link.path)
        }
      })
    },

    linkDataSelector(link) {
      return link.path === "/me/my-content"
        ? MVT_SELECTORS.TABBAR_YOU
        : link.path === "/"
        ? MVT_SELECTORS.TABBAR_HOME
        : null
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.v-tabbar__content {
  height: calc(100% - $made-with-movement-badge--height);

  @include hide-scroll-bars;
}

.v-tabbar__logo {
  display: none;
}

.v-tabbar__item {
  display: flex;
  flex-direction: column;
  text-align: center;
  position: relative;
  flex-grow: 1;
  flex-basis: 0;
  width: auto;

  &:hover {
    cursor: pointer;
  }

  .v-icon {
    font-size: 1.6rem;
    margin: 0 auto;
  }

  .v-link {
    display: flex;
    height: 56px;
    flex-direction: column;
    justify-content: center;
    text-decoration: none;
    color: $color-ui--grey-50;

    &.--active {
      color: var(--primary-brand-color-hex);

      svg {
        path,
        rect {
          stroke: var(--primary-brand-color-hex);
          fill: var(--primary-brand-color-hex);
        }
      }
    }
  }

  .marker-dot {
    background: $color-ui--green-dot;
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    z-index: 100;
  }
}

.tabbar__text {
  font-size: 0.6rem;
  font-weight: 500;
  text-transform: uppercase;
  margin-top: 3px;
}

// Transition
.tabbar-leave-enter-active,
.tabbar-leave-leave-active {
  transition: all 0.4s;
}
.tabbar-leave-enter,
.tabbar-leave-leave-to {
  opacity: 0;
  transform: translateY(57px);
}

.v-tabbar__content.--vertical {
  overflow-y: scroll;

  .v-toolbar-logo {
    max-height: 1.85rem;
    margin: 2rem 0 1rem;
    margin-left: 1rem;
    max-width: 75%;
  }

  .v-tabbar__navigation-wrapper {
    padding-top: 1rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    margin-bottom: $size--4;

    & > .container {
      flex-direction: column;
    }
  }

  .v-tabbar__logo {
    display: block;
  }

  .v-tabbar__item {
    text-align: left;
    border-radius: var(--input-border-radius);
    padding: 0rem 0.5rem;

    .v-link {
      height: 52px;
      flex-direction: row;
      align-items: center;
      justify-content: start;
      color: $color-ui--grey-70;

      .v-icon {
        margin: 0;
        margin-right: $size--3;
        transition: transform 0.1s ease-in;
      }

      &.--active,
      &.--active:hover {
        color: var(--primary-brand-color-hex);

        .tabbar__text {
          font-weight: $font-weight--semi-bold;
        }

        .v-icon {
          color: var(--primary-brand-color-hex);
        }
      }
    }

    &:hover {
      background: $color-ui--grey-15;

      .v-icon {
        transform: scale(1.07);
        color: $color-ui--grey-90;
      }
    }

    .tabbar__text {
      font-size: 1rem;
      margin-top: 0;
      text-transform: none;
    }
  }

  // Transition
  .tabbar-leave-enter-active,
  .tabbar-leave-leave-active {
    transition: all 0.4s;
  }
  .tabbar-leave-enter,
  .tabbar-leave-leave-to {
    opacity: 0;
    transform: translateX(-57px);
  }

  .marker-dot {
    top: 0.5rem;
    left: 0.25rem;
  }
}

[dir="rtl"] {
  .v-tabbar__content.--vertical {
    .v-tabbar__item {
      .v-link {
        .v-icon {
          margin: 0;
          margin-left: $size--3;
        }
      }
    }
  }
}

.--dark-mode {
  @media screen and (min-width: $breakpoint--lg) {
    .v-tabbar {
      border-right: 1px solid $color-ui--grey-15;

      .v-link.--active {
        color: $color-ui--black;

        svg {
          path,
          rect {
            stroke: var(--primary-brand-color-hex);
            fill: var(--primary-brand-color-hex);
          }
        }
      }
    }
  }
}
</style>
